import { useQuery } from '@tanstack/react-query'
import { format, parseISO } from 'date-fns'

import type { AdminUserVmDto } from './useAllVms'
import { allVmsQueryOptions } from './useAllVms'
import { buildDayBuckets } from './utils'

export interface VmReadinessPoint {
  date: string
  avgReadySeconds: number | null
  count: number
}

function buildVmReadiness(vms: AdminUserVmDto[]): VmReadinessPoint[] {
  const { days, toKey, toLabel } = buildDayBuckets()

  const totalsByDay = new Map<string, number>()
  const countsByDay = new Map<string, number>()
  for (const day of days) {
    const key = toKey(day)
    totalsByDay.set(key, 0)
    countsByDay.set(key, 0)
  }

  const startKey = toKey(days[0])

  for (const vm of vms) {
    if (!vm.readySince) continue
    const readyDate = parseISO(vm.readySince)
    const createdDate = parseISO(vm.createdAt)
    const diffSeconds = (readyDate.getTime() - createdDate.getTime()) / 1000
    if (!Number.isFinite(diffSeconds) || diffSeconds < 0) continue

    const bucketKey = format(readyDate, 'yyyy-MM-dd')
    if (bucketKey < startKey || !totalsByDay.has(bucketKey)) continue

    totalsByDay.set(bucketKey, (totalsByDay.get(bucketKey) ?? 0) + diffSeconds)
    countsByDay.set(bucketKey, (countsByDay.get(bucketKey) ?? 0) + 1)
  }

  return days.map((day) => {
    const key = toKey(day)
    const count = countsByDay.get(key) ?? 0
    const total = totalsByDay.get(key) ?? 0
    return {
      date: toLabel(day),
      avgReadySeconds: count > 0 ? total / count : null,
      count,
    }
  })
}

export function useVmReadiness() {
  return useQuery({ ...allVmsQueryOptions, select: buildVmReadiness })
}
